import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SidebarModuleDTO } from '../../types/sidebar';
import { useSidebar } from '../../hooks/sidebar/useSidebar';
import SidebarModuleLinkV2 from './SidebarModuleLinkV2';
import SidebarModuleHeader from './SidebarModuleHeader';
import SidebarModuleMenus from './SidebarModuleMenus';
import { useEscapeKey } from '../../hooks/useEscapeKey';

/**
 * 📦 SidebarModuleV2
 *
 * Controla el comportamiento visual y funcional de un módulo en el sidebar.
 * - Si `hasChildren = false`, muestra un enlace directo.
 * - Si `hasChildren = true`, carga menús (lazy load solo una vez).
 * - Marca el módulo como activo si la ruta coincide o pertenece a alguno de sus menús.
 * - Actualiza el estado global `isModuleActive` según interacción.
 */
const SidebarModuleV2: React.FC<{
  module: SidebarModuleDTO;
  sidebarExpanded: boolean;
  setSidebarExpanded: (expanded: boolean) => void;
  compactMode?: boolean;
}> = ({ module, sidebarExpanded, setSidebarExpanded, compactMode = false }) => {
  const { fetchModuleMenus, getModuleMenus, activateModule, deactivateModule } =
    useSidebar();

  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(
    () => getModuleMenus(module.code).length > 0
  );
  const moduleRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  // ===========================================================
  // 🔹 Cargar menús (solo la primera vez)
  // ===========================================================
  const handleToggle = async () => {
    if (module.disabled) return;

    if (module.hasChildren) {
      if (!loaded) {
        await fetchModuleMenus(module.code);
        setLoaded(true);
      }
      sidebarExpanded ? setOpen((prev) => !prev) : setSidebarExpanded(true);
    }
  };

  // ===========================================================
  // 🔹 Cerrar al hacer clic fuera
  // ===========================================================
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        open &&
        moduleRef.current &&
        !moduleRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        deactivateModule(); // 🚫 marcar como inactivo
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [open]);

  // ===========================================================
  // 🔹 Cerrar con tecla Escape
  // ===========================================================
  useEscapeKey(() => {
    if (open) {
      setOpen(false);
      deactivateModule();
    }
  });

  // ===========================================================
  // 🔹 Obtener menús del módulo actual
  // ===========================================================
  const moduleMenus = getModuleMenus(module.code);

  const isMenuRouteActive = (menus: any[], currentPath: string): boolean => {
    return menus.some((menu) => {
      const menuActive =
        menu.route &&
        (currentPath === menu.route ||
          currentPath.startsWith(menu.route + '/'));

      const childrenActive =
        menu.children?.length > 0
          ? isMenuRouteActive(menu.children, currentPath)
          : false;

      return menuActive || childrenActive;
    });
  };

  // ===========================================================
  // 🔹 Detectar si el módulo está activo
  // ===========================================================
  const isActive =
    (module.route &&
      (pathname === module.route || pathname.startsWith(module.route + '/'))) ||
    isMenuRouteActive(moduleMenus, pathname);

  // ===========================================================
  // 🔹 Abrir automáticamente si alguno de sus menús está activo
  // ===========================================================
  useEffect(() => {
    if (isActive && !open && module.hasChildren) {
      setOpen(true);
      activateModule(); // ✅ marcar activo globalmente

      if (!loaded) {
        fetchModuleMenus(module.code);
        setLoaded(true);
      }
    } else if (!isActive && open) {
      deactivateModule(); // 🚫 desactivar si ya no está activo
    }
  }, [isActive]);

  // ===========================================================
  // 🎨 Renderizado
  // ===========================================================
  if (!module.hasChildren) {
    return (
      <SidebarModuleLinkV2
        module={module}
        compactMode={compactMode}
        sidebarExpanded={sidebarExpanded}
      />
    );
  }

  return (
    <div ref={moduleRef} className="mb-2">
      <SidebarModuleHeader
        module={module}
        open={open}
        onToggle={handleToggle}
        isActive={isActive}
        compactMode={compactMode}
        sidebarExpanded={sidebarExpanded}
      />
      {open && !module.disabled && sidebarExpanded && (
        <SidebarModuleMenus menus={moduleMenus} />
      )}
    </div>
  );
};

export default SidebarModuleV2;
